import ArticleContent from 'components/pages/blog/ArticleContent'
import BackToList from 'components/shared/BackToList'
import SliceManager from 'components/shared/SliceManager'
import IntegrationTopics from 'components/shared/IntegrationTopics'
import delve from 'dlv'
import React from 'react'
import { getDataDependencies, buildStaticPaths } from 'services/api'
import fetchAllSlugs from 'src/lib/Strapi/fetchAllSlugs'

import PreviewBanner from 'src/components/shared/PreviewBanner'
import fetchContentType from 'src/lib/Strapi/fetchContentType'
import { revalidating } from 'src/lib/staticProps'

const Integration = ({ pageData, preview }) => {
  const {
    id,
    title,
    description,
    seo,
    image,
    publishedAt,
    user,
    content,
    slices,
    url,
    integration_topics,
    integration_tags,
  } = pageData

  const metaTitle = delve(seo, 'metaTitle')
  const metaDescription = delve(seo, 'metaDescription')

  return (
    <>
      {/*
        Workaround for Next.js bug: CSS modules loaded via dynamic imports with
        ssr:false don't get their stylesheets applied during client-side navigation.
        The LoadMore component styles are loaded through:
        SliceManager (dynamic) -> IntegrationsCardsWithIntro -> CardsWithIntro -> LoadMore
        @see https://github.com/vercel/next.js/issues/33286
        @see https://github.com/vercel/next.js/issues/49871
      */}
      <style jsx global>{`
        [class*='LoadMore_buttonContainer'] {
          text-align: center;
        }
        [class*='LoadMore_button'] {
          display: inline-flex;
          text-transform: uppercase;
          align-items: center;
          transform: translateZ(1000px);
        }
        [class*='LoadMore_button'] button,
        [class*='LoadMore_button'] a {
          text-transform: uppercase;
        }
        [class*='LoadMore_button'][class*='loading'] {
          pointer-events: none;
          cursor: not-allowed;
          opacity: 0.5;
        }
        [class*='LoadMore_smallArrow'] {
          width: 10px;
          transform: rotate(90deg);
          margin-left: 10px;
          margin-right: -10px;
        }
      `}</style>

      {preview && <PreviewBanner />}
      <div className="container">
        <BackToList
          text="Back to integrations"
          url={url}
          contentType="single"
        />
        <ArticleContent
          theme="purple"
          title={title ? `Integrate ${title} with Strapi` : metaTitle}
          subtitle={description || metaDescription}
          image={image}
          author={{
            name: delve(user, 'username'),
            description: delve(user, 'description'),
            job: delve(user, 'job.name'),
            date: publishedAt,
            image: delve(user, 'avatar'),
          }}
          content={
            `***These integration guides are not official documentation and the Strapi Support Team will not provide assistance with them***.\n\n` +
            content
          }
          comments={false}
        />

        {slices && (
          <SliceManager
            slices={slices}
            customOptions={{
              'slices.integration-cards-grid': {
                excludeIds: [id],
                filterTagIds:
                  integration_tags?.data?.map((tag) => tag.id) || [],
              },
            }}
          />
        )}
        {integration_topics && (
          <IntegrationTopics topics={integration_topics} integration={title} />
        )}
      </div>
    </>
  )
}

export async function getStaticPaths() {
  const data = await fetchAllSlugs('integrations')
  return buildStaticPaths(data, 'integrations')
}

export async function getStaticProps({ preview = null, params }) {
  const data = await fetchContentType(
    'integrations',
    `filters[slug]=${params.slug}${preview ? '&publicationState=preview' : ''}`
  )
  const pageData = await getDataDependencies(delve(data, '0'), preview)

  if (data?.length === 0 || !data) {
    return {
      props: {},
      notFound: true,
    }
  }

  return revalidating({
    props: {
      pageData: {
        ...pageData,
        url: '/integrations',
      },
      preview,
    },
  })
}

export default Integration
